import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Filter,
  Bike,
  Car,
  Eye,
  UserPlus,
  CheckCircle,
  XCircle,
  RefreshCw,
  MapPin,
  Phone,
  Clock,
  Search,
  Calendar,
  X,
  Navigation,
  Route,
  Store,
  Package,
} from "lucide-react";
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

// Leaflet marker icons
const pickupIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const dropoffIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const driverIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Map bounds fitter component
const FitBounds = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.length > 0) {
      map.fitBounds(bounds, { padding: [30, 30] });
    }
  }, [bounds, map]);
  return null;
};

export const RidesManagement = () => {
  const { user } = useAuth();
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [driverFilter, setDriverFilter] = useState("all");
  const [bookingTypeFilter, setBookingTypeFilter] = useState("all");
  // Default date filters to today
  const [dateFrom, setDateFrom] = useState(() => new Date().toISOString().split('T')[0]);
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [searchTerm, setSearchTerm] = useState("");
  const [rideTypeFilter, setRideTypeFilter] = useState("bike_taxi"); // New: bike_taxi or food_delivery
  const [cities, setCities] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [restaurantFilter, setRestaurantFilter] = useState("all");
  const [customerFilter, setCustomerFilter] = useState("");
  const [selectedRide, setSelectedRide] = useState(null);
  // Food delivery nearby riders
  const [foodDeliveryNearbyRiders, setFoodDeliveryNearbyRiders] = useState([]);
  const [loadingFoodDeliveryRiders, setLoadingFoodDeliveryRiders] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState("");
  const [cancelReason, setCancelReason] = useState("");
  const [refundAmount, setRefundAmount] = useState("");
  const [finalFare, setFinalFare] = useState("");
  // Driver tracking state
  const [showTrackingMap, setShowTrackingMap] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  const [loadingTracking, setLoadingTracking] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [totalRides, setTotalRides] = useState(0);
  // Geofencing status for rides list
  const [geofencingStatus, setGeofencingStatus] = useState({});
  
  // Nearest drivers for pending rides
  const [nearestDrivers, setNearestDrivers] = useState([]);
  const [loadingNearestDrivers, setLoadingNearestDrivers] = useState(false);

  useEffect(() => {
    if (user?.id) {
      fetchCitiesAndDrivers();
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) {
      fetchRides();
    }
  }, [statusFilter, cityFilter, driverFilter, bookingTypeFilter, rideTypeFilter, dateFrom, dateTo, user?.id]);

  // Fetch geofencing status when rides change
  useEffect(() => {
    if (rides.length > 0) {
      fetchGeofencingStatus();
    }
  }, [rides]);

  const fetchGeofencingStatus = async () => {
    try {
      // Get ride IDs that have drivers assigned
      const rideIds = rides
        .filter(r => r.driver_id)
        .map(r => r.id)
        .slice(0, 50); // Limit to 50
      
      if (rideIds.length === 0) return;
      
      const res = await axios.post(`${API}/admin/rides/batch-geofencing`, rideIds);
      setGeofencingStatus(res.data);
    } catch (err) {
      console.error("Failed to fetch geofencing status", err);
    }
  };

  const fetchCitiesAndDrivers = async () => {
    try {
      const [citiesRes, driversRes] = await Promise.all([
        axios.get(`${API}/cities?admin_id=${user.id}`),
        axios.get(`${API}/admin/drivers?admin_id=${user.id}`)
      ]);
      // Sort cities alphabetically
      const sortedCities = (citiesRes.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setCities(sortedCities);
      setDrivers(driversRes.data || []);
      
      // Fetch unique restaurants from food delivery orders
      try {
        const restaurantsRes = await axios.get(`${API}/quick-commerce/reports/orders/filters?admin_id=${user.id}`);
        if (restaurantsRes.data?.restaurants) {
          setRestaurants(restaurantsRes.data.restaurants);
        }
      } catch (err) {
        console.error("Failed to load restaurants", err);
      }
    } catch (err) {
      console.error("Failed to load cities/drivers", err);
    }
  };

  const fetchRides = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (cityFilter !== "all") params.append("city_id", cityFilter);
      if (driverFilter !== "all") params.append("driver_id", driverFilter);
      if (bookingTypeFilter !== "all") params.append("booking_type", bookingTypeFilter);
      if (rideTypeFilter !== "all") params.append("ride_type", rideTypeFilter);
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (searchTerm) params.append("search", searchTerm);
      
      const res = await axios.get(`${API}/admin/${user.id}/rides?${params.toString()}`);
      const ridesData = res.data.rides || res.data;
      setRides(ridesData);
      setTotalRides(res.data.total || ridesData.length);
    } catch (err) {
      toast.error("Failed to load rides");
    } finally {
      setLoading(false);
    }
  };

  const fetchRideDetails = async (rideId) => {
    try {
      const res = await axios.get(`${API}/admin/rides/${rideId}/details`);
      setSelectedRide(res.data.ride);
      setAvailableDrivers(res.data.available_drivers || []);
    } catch (err) {
      toast.error("Failed to load ride details");
    }
  };

  const fetchDriverTracking = async (rideId) => {
    setLoadingTracking(true);
    try {
      const res = await axios.get(`${API}/admin/rides/${rideId}/driver-tracking`);
      setTrackingData(res.data);
      setShowTrackingMap(true);
    } catch (err) {
      toast.error("Failed to load driver tracking data");
    } finally {
      setLoadingTracking(false);
    }
  };

  const handleViewRide = async (ride) => {
    // Reset tracking state when opening a new ride
    setShowTrackingMap(false);
    setTrackingData(null);
    await fetchRideDetails(ride.id);
    setShowDetailsDialog(true);
  };

  const handleAssignDriver = async () => {
    if (!selectedDriver) {
      toast.error("Please select a driver");
      return;
    }
    setActionLoading(true);
    try {
      await axios.put(`${API}/admin/rides/${selectedRide.id}/assign`, {
        driver_id: selectedDriver,
      });
      toast.success("Driver assigned successfully");
      setShowAssignDialog(false);
      setShowDetailsDialog(false);
      setSelectedDriver("");
      fetchRides();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assign driver");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateStatus = async (status) => {
    setActionLoading(true);
    try {
      await axios.put(`${API}/admin/rides/${selectedRide.id}/status?status=${status}`);
      toast.success(`Ride status updated to ${status}`);
      await fetchRideDetails(selectedRide.id);
      fetchRides();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCompleteRide = async () => {
    setActionLoading(true);
    try {
      const data = {};
      if (finalFare) data.final_fare = parseFloat(finalFare);
      await axios.put(`${API}/admin/rides/${selectedRide.id}/complete`, data);
      toast.success("Ride completed successfully");
      setShowCompleteDialog(false);
      setShowDetailsDialog(false);
      setFinalFare("");
      fetchRides();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to complete ride");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelRide = async () => {
    if (!cancelReason.trim()) {
      toast.error("Please provide a cancellation reason");
      return;
    }
    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/rides/${selectedRide.id}/cancel`, {
        reason: cancelReason,
        refund_amount: refundAmount ? parseFloat(refundAmount) : null,
      });
      toast.success("Ride cancelled successfully");
      setShowCancelDialog(false);
      setShowDetailsDialog(false);
      setCancelReason("");
      setRefundAmount("");
      fetchRides();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel ride");
    } finally {
      setActionLoading(false);
    }
  };

  // Fetch nearest drivers for a pending ride
  const fetchNearestDrivers = async (ride) => {
    if (!ride?.pickup?.lat || !ride?.pickup?.lng || ride.status !== "pending") {
      setNearestDrivers([]);
      return;
    }
    
    setLoadingNearestDrivers(true);
    try {
      const params = new URLSearchParams({
        lat: ride.pickup.lat,
        lng: ride.pickup.lng,
        vehicle_type: ride.vehicle_type,
        limit: 20
      });
      if (ride.city_id) params.append("city_id", ride.city_id);
      
      const res = await axios.get(`${API}/rides/nearest-drivers?${params.toString()}`);
      setNearestDrivers(res.data.drivers || []);
    } catch (err) {
      console.error("Failed to fetch nearest drivers:", err);
      setNearestDrivers([]);
    } finally {
      setLoadingNearestDrivers(false);
    }
  };

  // Fetch nearest drivers when viewing a pending ride's details
  useEffect(() => {
    if (selectedRide && showDetailsDialog && selectedRide.status === "pending") {
      fetchNearestDrivers(selectedRide);
    } else {
      setNearestDrivers([]);
    }
  }, [selectedRide?.id, showDetailsDialog]);

  // Fetch nearby food delivery riders for pending food delivery orders
  const fetchFoodDeliveryNearbyRiders = async (ride) => {
    if (!ride?.pickup?.lat || !ride?.pickup?.lng || ride.driver_id) {
      setFoodDeliveryNearbyRiders([]);
      return;
    }
    
    setLoadingFoodDeliveryRiders(true);
    try {
      const params = new URLSearchParams({
        lat: ride.pickup.lat,
        lng: ride.pickup.lng,
        limit: 20
      });
      if (ride.city_id) params.append("city_id", ride.city_id);
      
      // Get nearby food delivery enabled riders
      const res = await axios.get(`${API}/quick-commerce/nearby-food-riders?${params.toString()}`);
      setFoodDeliveryNearbyRiders(res.data.riders || []);
    } catch (err) {
      console.error("Failed to fetch nearby food delivery riders:", err);
      setFoodDeliveryNearbyRiders([]);
    } finally {
      setLoadingFoodDeliveryRiders(false);
    }
  };

  // Fetch nearby food delivery riders when viewing a food delivery order without driver
  useEffect(() => {
    if (selectedRide && showDetailsDialog && selectedRide.ride_type === "food_delivery" && !selectedRide.driver_id) {
      fetchFoodDeliveryNearbyRiders(selectedRide);
    } else {
      setFoodDeliveryNearbyRiders([]);
    }
  }, [selectedRide?.id, showDetailsDialog, selectedRide?.driver_id]);

  const statusOptions = [
    { value: "all", label: "All Status" },
    { value: "pending", label: "Pending" },
    { value: "accepted", label: "Accepted" },
    { value: "arrived", label: "Arrived" },
    { value: "in_progress", label: "In Progress" },
    { value: "completed", label: "Completed" },
    { value: "cancelled", label: "Cancelled" },
  ];

  const getStatusBadge = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-700",
      accepted: "bg-blue-100 text-blue-700",
      arrived: "bg-purple-100 text-purple-700",
      in_progress: "bg-indigo-100 text-indigo-700",
      completed: "bg-green-100 text-green-700",
      cancelled: "bg-red-100 text-red-700",
    };
    return colors[status] || "bg-gray-100 text-gray-700";
  };

  // Format date/time to IST
  const formatISTDateTime = (dateStr, showTime = true) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    const options = {
      timeZone: "Asia/Kolkata",
      day: "numeric",
      month: "short",
      ...(showTime && { hour: "2-digit", minute: "2-digit", hour12: true }),
    };
    return date.toLocaleString("en-IN", options);
  };

  // Get pickup display - handle "Current Location" case
  const getPickupDisplay = (ride) => {
    const address = ride.pickup?.address || "";
    if (address.toLowerCase() === "current location" || address === "") {
      // Fallback to city name or coordinates
      if (ride.city_name) {
        return `${ride.city_name} (GPS)`;
      }
      if (ride.pickup?.lat && ride.pickup?.lng) {
        return `${ride.pickup.lat.toFixed(4)}, ${ride.pickup.lng.toFixed(4)}`;
      }
      return "GPS Location";
    }
    return address;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const bookingTypeOptions = [
    { value: "all", label: "All Types" },
    { value: "one_way", label: "One Way" },
    { value: "two_way", label: "Round Trip" },
    { value: "hourly", label: "Hourly" },
    { value: "daily", label: "Daily" },
    { value: "package", label: "Package" },
  ];

  return (
    <div data-testid="rides-management">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Rides Management</h1>
          <p className="text-sm text-muted-foreground">Total: {totalRides} rides</p>
        </div>
        <Button variant="outline" onClick={fetchRides}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Ride Type Tabs */}
      <div className="flex gap-2 mb-4">
        <Button
          variant={rideTypeFilter === "bike_taxi" ? "default" : "outline"}
          onClick={() => setRideTypeFilter("bike_taxi")}
          className="flex items-center gap-2"
          data-testid="ride-type-bike-taxi"
        >
          <Bike className="w-4 h-4" />
          Bike Taxi
        </Button>
        <Button
          variant={rideTypeFilter === "food_delivery" ? "default" : "outline"}
          onClick={() => setRideTypeFilter("food_delivery")}
          className="flex items-center gap-2"
          data-testid="ride-type-food-delivery"
        >
          <Package className="w-4 h-4" />
          Food Delivery
        </Button>
      </div>

      {/* Filters Section */}
      <div className="bg-card border rounded-lg p-4 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <span className="font-medium text-sm">Filters</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {/* Status Filter */}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* City Filter */}
          <Select value={cityFilter} onValueChange={setCityFilter}>
            <SelectTrigger data-testid="city-filter">
              <SelectValue placeholder="City" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Cities</SelectItem>
              {cities.map((city) => (
                <SelectItem key={city.id} value={city.id}>
                  {city.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Driver Filter */}
          <Select value={driverFilter} onValueChange={setDriverFilter}>
            <SelectTrigger data-testid="driver-filter">
              <SelectValue placeholder="Rider" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Riders</SelectItem>
              {drivers.map((driver) => (
                <SelectItem key={driver.id} value={driver.id}>
                  {driver.name || driver.phone}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Booking Type Filter - Only for Bike Taxi */}
          {rideTypeFilter === "bike_taxi" && (
            <Select value={bookingTypeFilter} onValueChange={setBookingTypeFilter}>
              <SelectTrigger data-testid="booking-type-filter">
                <SelectValue placeholder="Booking Type" />
              </SelectTrigger>
              <SelectContent>
                {bookingTypeOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Restaurant Filter - Only for Food Delivery */}
          {rideTypeFilter === "food_delivery" && (
            <Select value={restaurantFilter} onValueChange={setRestaurantFilter}>
              <SelectTrigger data-testid="restaurant-filter">
                <SelectValue placeholder="Restaurant" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Restaurants</SelectItem>
                {restaurants.map((restaurant, idx) => (
                  <SelectItem key={restaurant.id || idx} value={restaurant.name || restaurant}>
                    {restaurant.name || restaurant}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Customer Filter - Only for Food Delivery */}
          {rideTypeFilter === "food_delivery" && (
            <div className="relative">
              <Search className="absolute left-2 top-2.5 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Customer..."
                value={customerFilter}
                onChange={(e) => setCustomerFilter(e.target.value)}
                className="pl-8"
                data-testid="customer-filter"
              />
            </div>
          )}

          {/* Date From */}
          <div className="relative">
            <Calendar className="absolute left-2 top-2.5 w-4 h-4 text-muted-foreground" />
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="pl-8"
              placeholder="From"
              data-testid="date-from"
            />
          </div>

          {/* Date To */}
          <div className="relative">
            <Calendar className="absolute left-2 top-2.5 w-4 h-4 text-muted-foreground" />
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="pl-8"
              placeholder="To"
              data-testid="date-to"
            />
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2 top-2.5 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchRides()}
              className="pl-8"
              data-testid="search-input"
            />
          </div>
        </div>
      </div>

      <div className="bg-card rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {rideTypeFilter === "food_delivery" ? (
                  <>
                    <TableHead>City</TableHead>
                    <TableHead>Restaurant</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Rider</TableHead>
                    <TableHead>Order Placed</TableHead>
                    <TableHead>Completed</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Order Amt</TableHead>
                    <TableHead>Delivery Fee</TableHead>
                    <TableHead>Store Payout</TableHead>
                    <TableHead className="text-center">COD</TableHead>
                    <TableHead className="text-center">CDC</TableHead>
                    <TableHead>Actions</TableHead>
                  </>
                ) : (
                  <>
                    <TableHead>Customer</TableHead>
                    <TableHead>Driver</TableHead>
                    <TableHead>City</TableHead>
                    <TableHead>Route</TableHead>
                    <TableHead>Vehicle</TableHead>
                    <TableHead>Distance</TableHead>
                    <TableHead>Fare</TableHead>
                    <TableHead>Commission</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-center">Pickup ✓</TableHead>
                    <TableHead>Booked (IST)</TableHead>
                    <TableHead>Actions</TableHead>
                  </>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rides.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={rideTypeFilter === "food_delivery" ? 13 : 12} className="text-center py-8 text-muted-foreground">
                    No {rideTypeFilter === "food_delivery" ? "food delivery orders" : "rides"} found
                  </TableCell>
                </TableRow>
              ) : rideTypeFilter === "food_delivery" ? (
                // Food Delivery Table Rows
                rides.map((ride) => (
                  <TableRow key={ride.id} data-testid={`ride-row-${ride.id}`}>
                    {/* City */}
                    <TableCell>
                      <span className="text-sm">{ride.city_name || '-'}</span>
                    </TableCell>
                    {/* Restaurant */}
                    <TableCell>
                      <div className="max-w-[150px]">
                        <p className="font-medium text-sm truncate" title={ride.business_name || ride.pickup?.address}>
                          {ride.business_name || ride.pickup?.address?.split(',')[0] || '-'}
                        </p>
                        {ride.business_phone && (
                          <p className="text-xs text-muted-foreground">{ride.business_phone}</p>
                        )}
                      </div>
                    </TableCell>
                    {/* Customer */}
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm">{ride.customer_name || '-'}</p>
                        {ride.customer_phone && (
                          <p className="text-xs text-muted-foreground">{ride.customer_phone}</p>
                        )}
                      </div>
                    </TableCell>
                    {/* Rider */}
                    <TableCell>
                      {ride.driver_name ? (
                        <div>
                          <p className="font-medium text-sm">{ride.driver_name}</p>
                          <p className="text-xs text-muted-foreground">{ride.driver_phone}</p>
                        </div>
                      ) : (
                        <span className="text-amber-600 italic text-sm">Unassigned</span>
                      )}
                    </TableCell>
                    {/* Order Placed Time */}
                    <TableCell>
                      <div className="text-xs">
                        <p className="font-medium">{formatISTDateTime(ride.created_at, false)}</p>
                        <p className="text-muted-foreground">{formatISTDateTime(ride.created_at, true).split(",").pop()?.trim()}</p>
                      </div>
                    </TableCell>
                    {/* Completed Time */}
                    <TableCell>
                      {ride.completed_at ? (
                        <div className="text-xs">
                          <p className="font-medium">{formatISTDateTime(ride.completed_at, false)}</p>
                          <p className="text-muted-foreground">{formatISTDateTime(ride.completed_at, true).split(",").pop()?.trim()}</p>
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-xs">-</span>
                      )}
                    </TableCell>
                    {/* Status */}
                    <TableCell>
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                        ride.status === "completed" ? "bg-green-100 text-green-700" :
                        ride.status === "cancelled" ? "bg-red-100 text-red-700" :
                        ride.status === "in_progress" || ride.status === "picked_up" ? "bg-blue-100 text-blue-700" :
                        ride.status === "accepted" ? "bg-purple-100 text-purple-700" :
                        "bg-amber-100 text-amber-700"
                      }`}>
                        {ride.status?.replace(/_/g, " ")}
                      </span>
                    </TableCell>
                    {/* Order Amount (Total) */}
                    <TableCell>
                      <span className="font-numbers text-sm">
                        ₹{ride.total_amount || ride.cod_amount || 0}
                      </span>
                    </TableCell>
                    {/* Delivery Fee */}
                    <TableCell>
                      <span className="font-numbers font-semibold text-green-600">
                        ₹{ride.fare || ride.offer_rate || 0}
                      </span>
                    </TableCell>
                    {/* Store Payout */}
                    <TableCell>
                      {ride.is_cdc_order && ride.shop_payout_amount > 0 ? (
                        <span className="font-numbers text-purple-600">₹{ride.shop_payout_amount}</span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    {/* COD Flag */}
                    <TableCell className="text-center">
                      {ride.cod_amount > 0 ? (
                        <span className="bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0.5 rounded font-bold">COD</span>
                      ) : (
                        <span className="text-muted-foreground text-xs">-</span>
                      )}
                    </TableCell>
                    {/* CDC Flag */}
                    <TableCell className="text-center">
                      {ride.is_cdc_order ? (
                        <span className="bg-purple-100 text-purple-700 text-[10px] px-1.5 py-0.5 rounded font-bold">CDC</span>
                      ) : (
                        <span className="text-muted-foreground text-xs">-</span>
                      )}
                    </TableCell>
                    {/* Actions */}
                    <TableCell>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleViewRide(ride)}
                        data-testid={`view-ride-${ride.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                // Bike Taxi Table Rows (existing code)
                rides.map((ride) => (
                  <TableRow key={ride.id} data-testid={`ride-row-${ride.id}`} className={geofencingStatus[ride.id]?.is_suspicious ? "bg-red-50" : ""}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{ride.customer_name}</p>
                        <p className="text-xs text-muted-foreground">{ride.customer_phone}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      {ride.driver_name ? (
                        <div>
                          <p className="font-medium">{ride.driver_name}</p>
                          <p className="text-xs text-muted-foreground">{ride.driver_phone}</p>
                        </div>
                      ) : (
                        <span className="text-muted-foreground italic">Not assigned</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{ride.city_name || ride.driver_city_name || '-'}</span>
                    </TableCell>
                    <TableCell>
                      <div className="max-w-[150px]">
                        <p className="text-xs text-muted-foreground truncate" title={ride.pickup?.address}>
                          <MapPin className="w-3 h-3 inline mr-1 text-green-500" />
                          {getPickupDisplay(ride)}
                        </p>
                        <p className="text-xs font-medium truncate mt-1">
                          → {ride.drop?.address || ride.dropoff?.address}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {ride.vehicle_type === "bike" ? (
                          <Bike className="w-4 h-4 text-primary" />
                        ) : (
                          <Car className="w-4 h-4 text-accent" />
                        )}
                        <span className="capitalize">{ride.vehicle_type}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="font-numbers text-sm">
                        {ride.distance_km ? `${ride.distance_km} km` : '-'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="text-right">
                        <span className="font-numbers font-semibold">₹{(ride.fare || 0) + (ride.tip_amount || 0)}</span>
                        {ride.tip_amount > 0 && (
                          <p className="text-xs text-green-600">+₹{ride.tip_amount} tip</p>
                        )}
                        {ride.cod_amount > 0 && (
                          <p className="text-xs text-amber-600">COD: ₹{ride.cod_amount}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {ride.status === "completed" ? (
                        <div className="text-right">
                          <span className="font-numbers font-semibold text-green-600">
                            ₹{ride.commission_amount || Math.round((ride.fare || 0) * 0.1)}
                          </span>
                          <p className="text-xs text-muted-foreground">
                            {ride.commission_type === "percentage" 
                              ? `${ride.commission_rate || 10}%` 
                              : ride.commission_type === "flat_per_ride"
                              ? "flat"
                              : "per ride"}
                          </p>
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(ride.status)}`}>
                        {ride.status?.replace("_", " ")}
                      </span>
                      {/* Cancellation reason */}
                      {ride.status === "cancelled" && ride.cancellation_reason && (
                        <p className="text-xs text-destructive mt-1 max-w-[120px] truncate" title={ride.cancellation_reason}>
                          {ride.cancellation_reason}
                        </p>
                      )}
                      {/* Rebook indicator */}
                      {ride.status === "cancelled" && ride.rebooked_ride_id && (
                        <span className="inline-flex items-center gap-1 text-xs text-blue-600 mt-1">
                          <RefreshCw className="w-3 h-3" />
                          Rebooked
                        </span>
                      )}
                    </TableCell>
                    {/* Pickup Verified Column */}
                    <TableCell className="text-center">
                      {!ride.driver_id ? (
                        <span className="text-gray-300">—</span>
                      ) : geofencingStatus[ride.id] ? (
                        <div className="flex flex-col items-center gap-1">
                          {geofencingStatus[ride.id].is_suspicious ? (
                            <span className="text-xl" title={geofencingStatus[ride.id].suspicion_reason}>⚠️</span>
                          ) : geofencingStatus[ride.id].reached_pickup ? (
                            <span className="text-xl" title="Driver reached pickup location">✅</span>
                          ) : (
                            <span className="text-xl" title={`Min distance: ${geofencingStatus[ride.id].pickup_distance_m || 'N/A'}m`}>❌</span>
                          )}
                          {geofencingStatus[ride.id].pickup_distance_m != null && (
                            <span className="text-[10px] text-muted-foreground">
                              {geofencingStatus[ride.id].pickup_distance_m}m
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-300 text-xs">...</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div>
                        <span className="text-sm font-medium">
                          {formatISTDateTime(ride.created_at, false)}
                        </span>
                        <p className="text-xs text-muted-foreground">
                          {formatISTDateTime(ride.created_at, true).split(",").pop()?.trim()}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button size="sm" variant="ghost" onClick={() => handleViewRide(ride)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Ride Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={(open) => {
        setShowDetailsDialog(open);
        if (!open) {
          // Reset tracking state when closing dialog
          setShowTrackingMap(false);
          setTrackingData(null);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col p-0 [&>button]:hidden">
          <DialogHeader className="p-4 pb-2 border-b sticky top-0 bg-background z-10">
            <div className="flex items-center justify-between">
              <DialogTitle>Ride Details</DialogTitle>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => setShowDetailsDialog(false)}
                className="h-8 w-8"
                data-testid="close-ride-details"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </DialogHeader>
          {selectedRide && (
            <div className="p-4 space-y-4 overflow-y-auto flex-1">
              {/* Status Banner */}
              <div className={`p-3 rounded-lg ${getStatusBadge(selectedRide.status)}`}>
                <p className="font-medium">Status: {selectedRide.status?.replace("_", " ").toUpperCase()}</p>
                {selectedRide.cancellation_reason && (
                  <p className="text-sm mt-1">Reason: {selectedRide.cancellation_reason}</p>
                )}
              </div>

              {/* Status Timeline - Different for Bike Taxi vs Food Delivery */}
              <div className="p-4 bg-muted/30 rounded-lg">
                <p className="text-xs text-muted-foreground mb-3 font-medium">
                  {selectedRide.ride_type === "food_delivery" ? "Order Timeline (IST)" : "Ride Timeline (IST)"}
                </p>
                {selectedRide.ride_type === "food_delivery" ? (
                  // Food Delivery Timeline: Order Placed → Accepted → Picked Up → Delivered
                  <div className="flex items-start justify-between">
                    {/* Order Placed */}
                    <div className="flex flex-col items-center text-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${selectedRide.created_at ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-400'}`}>
                        <Clock className="w-4 h-4" />
                      </div>
                      <p className="text-xs font-medium mt-2">Ordered</p>
                      <p className="text-[10px] text-muted-foreground">
                        {formatISTDateTime(selectedRide.created_at)}
                      </p>
                    </div>

                    {/* Line */}
                    <div className={`flex-1 h-0.5 mt-4 mx-2 ${selectedRide.accepted_at ? 'bg-green-500' : 'bg-gray-200'}`} />

                    {/* Accepted */}
                    <div className="flex flex-col items-center text-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${selectedRide.accepted_at ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-400'}`}>
                        <CheckCircle className="w-4 h-4" />
                      </div>
                      <p className="text-xs font-medium mt-2">Accepted</p>
                      <p className="text-[10px] text-muted-foreground">
                        {selectedRide.accepted_at ? formatISTDateTime(selectedRide.accepted_at) : '-'}
                      </p>
                    </div>

                    {/* Line */}
                    <div className={`flex-1 h-0.5 mt-4 mx-2 ${selectedRide.picked_up_at ? 'bg-green-500' : 'bg-gray-200'}`} />

                    {/* Picked Up */}
                    <div className="flex flex-col items-center text-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${selectedRide.picked_up_at ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-400'}`}>
                        <Store className="w-4 h-4" />
                      </div>
                      <p className="text-xs font-medium mt-2">Picked Up</p>
                      <p className="text-[10px] text-muted-foreground">
                        {selectedRide.picked_up_at ? formatISTDateTime(selectedRide.picked_up_at) : '-'}
                      </p>
                    </div>

                    {/* Line */}
                    <div className={`flex-1 h-0.5 mt-4 mx-2 ${selectedRide.completed_at ? 'bg-green-500' : 'bg-gray-200'}`} />

                    {/* Delivered/Cancelled */}
                    <div className="flex flex-col items-center text-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${selectedRide.completed_at ? 'bg-green-500 text-white' : selectedRide.status === 'cancelled' ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-400'}`}>
                        {selectedRide.status === 'cancelled' ? <XCircle className="w-4 h-4" /> : <Package className="w-4 h-4" />}
                      </div>
                      <p className="text-xs font-medium mt-2">{selectedRide.status === 'cancelled' ? 'Cancelled' : 'Delivered'}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {selectedRide.status === 'cancelled' 
                          ? (selectedRide.cancelled_at ? formatISTDateTime(selectedRide.cancelled_at) : '-')
                          : (selectedRide.completed_at ? formatISTDateTime(selectedRide.completed_at) : '-')}
                      </p>
                    </div>
                  </div>
                ) : (
                  // Bike Taxi Timeline: Booked → Accepted → Arrived → Completed
                  <div className="flex items-start justify-between">
                    {/* Booked */}
                    <div className="flex flex-col items-center text-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${selectedRide.created_at ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-400'}`}>
                        <Clock className="w-4 h-4" />
                      </div>
                      <p className="text-xs font-medium mt-2">Booked</p>
                      <p className="text-[10px] text-muted-foreground">
                        {formatISTDateTime(selectedRide.created_at)}
                      </p>
                    </div>

                    {/* Line */}
                    <div className={`flex-1 h-0.5 mt-4 mx-2 ${selectedRide.accepted_at ? 'bg-green-500' : 'bg-gray-200'}`} />

                    {/* Accepted */}
                    <div className="flex flex-col items-center text-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${selectedRide.accepted_at ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-400'}`}>
                        <CheckCircle className="w-4 h-4" />
                      </div>
                      <p className="text-xs font-medium mt-2">Accepted</p>
                      <p className="text-[10px] text-muted-foreground">
                        {selectedRide.accepted_at ? formatISTDateTime(selectedRide.accepted_at) : '-'}
                      </p>
                    </div>

                    {/* Line */}
                    <div className={`flex-1 h-0.5 mt-4 mx-2 ${selectedRide.arrived_at ? 'bg-green-500' : 'bg-gray-200'}`} />

                    {/* Arrived */}
                    <div className="flex flex-col items-center text-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${selectedRide.arrived_at ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-400'}`}>
                        <MapPin className="w-4 h-4" />
                      </div>
                      <p className="text-xs font-medium mt-2">Arrived</p>
                      <p className="text-[10px] text-muted-foreground">
                        {selectedRide.arrived_at ? formatISTDateTime(selectedRide.arrived_at) : '-'}
                      </p>
                    </div>

                    {/* Line */}
                    <div className={`flex-1 h-0.5 mt-4 mx-2 ${selectedRide.completed_at ? 'bg-green-500' : 'bg-gray-200'}`} />

                    {/* Completed/Cancelled */}
                    <div className="flex flex-col items-center text-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${selectedRide.completed_at ? 'bg-green-500 text-white' : selectedRide.status === 'cancelled' ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-400'}`}>
                        {selectedRide.status === 'cancelled' ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                      </div>
                      <p className="text-xs font-medium mt-2">{selectedRide.status === 'cancelled' ? 'Cancelled' : 'Completed'}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {selectedRide.status === 'cancelled' 
                          ? (selectedRide.cancelled_at ? formatISTDateTime(selectedRide.cancelled_at) : '-')
                          : (selectedRide.completed_at ? formatISTDateTime(selectedRide.completed_at) : '-')}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Customer & Driver Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-2">Customer</p>
                  <p className="font-medium">{selectedRide.customer_name}</p>
                  <p className="text-sm flex items-center gap-1">
                    <Phone className="w-3 h-3" /> {selectedRide.customer_phone}
                  </p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-2">Driver</p>
                  {selectedRide.driver_name ? (
                    <>
                      <p className="font-medium">{selectedRide.driver_name}</p>
                      <p className="text-sm">{selectedRide.driver_phone}</p>
                      <p className="text-xs text-muted-foreground">{selectedRide.driver_vehicle_number}</p>
                    </>
                  ) : (
                    <p className="text-muted-foreground italic">Not assigned</p>
                  )}
                </div>
              </div>

              {/* Route Info */}
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="flex items-start gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-green-500 mt-1" />
                  <div>
                    <p className="text-xs text-muted-foreground">Pickup</p>
                    <p className="text-sm">{getPickupDisplay(selectedRide)}</p>
                    {selectedRide.pickup?.lat && selectedRide.pickup?.lng && (
                      <p className="text-[10px] text-muted-foreground">
                        {selectedRide.pickup.lat.toFixed(6)}, {selectedRide.pickup.lng.toFixed(6)}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-red-500 mt-1" />
                  <div>
                    <p className="text-xs text-muted-foreground">Drop</p>
                    <p className="text-sm">{(selectedRide.dropoff || selectedRide.drop)?.address}</p>
                  </div>
                </div>
              </div>

              {/* Distance Info */}
              {selectedRide.distance_km && (
                <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
                  <span className="text-muted-foreground flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Ride Distance
                  </span>
                  <span className="text-lg font-bold text-blue-600">{selectedRide.distance_km} km</span>
                </div>
              )}

              {/* Fare Info */}
              <div className="p-3 bg-primary/5 rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Base Fare</span>
                  <span className="font-semibold">₹{selectedRide.fare}</span>
                </div>
                {selectedRide.tip_amount > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-green-600">Tip</span>
                    <span className="font-semibold text-green-600">+₹{selectedRide.tip_amount}</span>
                  </div>
                )}
                <div className="flex items-center justify-between pt-2 border-t">
                  <span className="font-medium">Total to Collect</span>
                  <span className="text-xl font-bold text-primary">
                    ₹{(selectedRide.fare || 0) + (selectedRide.tip_amount || 0)}
                  </span>
                </div>
              </div>

              {/* OTP if assigned */}
              {selectedRide.otp && selectedRide.status !== "completed" && (
                <div className="p-3 bg-amber-50 rounded-lg text-center">
                  <p className="text-sm text-muted-foreground">Pickup OTP</p>
                  <p className="text-2xl font-bold text-amber-600">{selectedRide.otp}</p>
                </div>
              )}

              {/* Driver Location Tracking Button - Show when driver is assigned */}
              {selectedRide.driver_id && (
                <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-950/30 dark:to-purple-950/30 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Route className="w-5 h-5 text-indigo-600" />
                      <span className="font-medium text-indigo-700 dark:text-indigo-300">Driver Location Tracking</span>
                    </div>
                    <Button
                      size="sm"
                      variant={showTrackingMap ? "secondary" : "default"}
                      onClick={() => {
                        if (showTrackingMap) {
                          setShowTrackingMap(false);
                        } else {
                          fetchDriverTracking(selectedRide.id);
                        }
                      }}
                      disabled={loadingTracking}
                      className="gap-2"
                    >
                      {loadingTracking ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Navigation className="w-4 h-4" />
                      )}
                      {showTrackingMap ? "Hide Map" : "View Tracking"}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Track driver's path after accepting to verify if they went to pickup location
                  </p>

                  {/* Tracking Map */}
                  {showTrackingMap && trackingData && (
                    <div className="mt-3 space-y-3">
                      {/* Driver Info Banner */}
                      {trackingData.driver_phone && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 text-center">
                          <p className="text-sm font-medium text-blue-800">
                            Tracking: {trackingData.driver_name} ({trackingData.driver_phone})
                          </p>
                        </div>
                      )}

                      {/* Geofencing Alert */}
                      {trackingData.geofencing?.is_suspicious && (
                        <div className="bg-red-50 border-2 border-red-400 rounded-lg p-3">
                          <div className="flex items-center gap-2">
                            <span className="text-red-600 text-lg">⚠️</span>
                            <div>
                              <p className="text-sm font-bold text-red-700">Suspicious Activity Detected</p>
                              <p className="text-xs text-red-600">{trackingData.geofencing.suspicion_reason}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Geofencing Stats */}
                      <div className="grid grid-cols-2 gap-2">
                        <div className={`p-2 rounded-lg border-2 ${trackingData.geofencing?.reached_pickup ? 'bg-green-50 border-green-400' : 'bg-gray-50 border-gray-200'}`}>
                          <div className="flex items-center gap-2">
                            <span className="text-lg">
                              {trackingData.geofencing?.reached_pickup ? '✅' : '❌'}
                            </span>
                            <div>
                              <p className="text-xs font-medium">Reached Pickup</p>
                              <p className="text-[10px] text-muted-foreground">
                                {trackingData.geofencing?.pickup_min_distance_m != null 
                                  ? `Min distance: ${trackingData.geofencing.pickup_min_distance_m}m`
                                  : 'No tracking data'}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className={`p-2 rounded-lg border-2 ${trackingData.geofencing?.reached_dropoff ? 'bg-green-50 border-green-400' : 'bg-gray-50 border-gray-200'}`}>
                          <div className="flex items-center gap-2">
                            <span className="text-lg">
                              {trackingData.geofencing?.reached_dropoff ? '✅' : '❌'}
                            </span>
                            <div>
                              <p className="text-xs font-medium">Reached Drop</p>
                              <p className="text-[10px] text-muted-foreground">
                                {trackingData.geofencing?.dropoff_min_distance_m != null 
                                  ? `Min distance: ${trackingData.geofencing.dropoff_min_distance_m}m`
                                  : 'No tracking data'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Tracking Stats */}
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="bg-white dark:bg-gray-800 p-2 rounded-lg">
                          <p className="text-lg font-bold text-indigo-600">{trackingData.tracking_info?.total_points || 0}</p>
                          <p className="text-[10px] text-muted-foreground">Location Points</p>
                        </div>
                        <div className="bg-white dark:bg-gray-800 p-2 rounded-lg">
                          <p className="text-lg font-bold text-green-600">{trackingData.arrived_at ? "Yes" : "No"}</p>
                          <p className="text-[10px] text-muted-foreground">Marked Arrived</p>
                        </div>
                        <div className="bg-white dark:bg-gray-800 p-2 rounded-lg">
                          <p className="text-lg font-bold text-orange-600">{trackingData.ride_status}</p>
                          <p className="text-[10px] text-muted-foreground">Current Status</p>
                        </div>
                      </div>

                      {/* Map */}
                      <div className="h-[300px] rounded-lg overflow-hidden border-2 border-indigo-200">
                        <MapContainer
                          center={[
                            trackingData.pickup?.lat || 28.6139,
                            trackingData.pickup?.lng || 77.2090
                          ]}
                          zoom={14}
                          style={{ height: "100%", width: "100%" }}
                        >
                          <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          />
                          
                          {/* Pickup Marker */}
                          {trackingData.pickup?.lat && trackingData.pickup?.lng && 
                           !isNaN(trackingData.pickup.lat) && !isNaN(trackingData.pickup.lng) && (
                            <Marker 
                              position={[parseFloat(trackingData.pickup.lat), parseFloat(trackingData.pickup.lng)]}
                              icon={pickupIcon}
                            >
                              <Popup>
                                <strong>Pickup Location</strong><br/>
                                {trackingData.pickup_address || trackingData.pickup.address || "Pickup Point"}
                              </Popup>
                            </Marker>
                          )}
                          
                          {/* Dropoff Marker */}
                          {trackingData.dropoff?.lat && trackingData.dropoff?.lng && 
                           !isNaN(trackingData.dropoff.lat) && !isNaN(trackingData.dropoff.lng) && (
                            <Marker 
                              position={[parseFloat(trackingData.dropoff.lat), parseFloat(trackingData.dropoff.lng)]}
                              icon={dropoffIcon}
                            >
                              <Popup>
                                <strong>Drop Location</strong><br/>
                                {trackingData.dropoff_address || trackingData.dropoff.address || "Drop Point"}
                              </Popup>
                            </Marker>
                          )}
                          
                          {/* Driver's Current Location */}
                          {trackingData.driver_current_location?.lat && (
                            <Marker 
                              position={[
                                trackingData.driver_current_location.lat, 
                                trackingData.driver_current_location.lng
                              ]}
                              icon={driverIcon}
                            >
                              <Popup>
                                <strong>{trackingData.driver_name || "Driver"}</strong><br/>
                                Current Location<br/>
                                <span className="text-xs text-gray-500">
                                  {trackingData.driver_current_location.updated_at 
                                    ? `Updated: ${new Date(trackingData.driver_current_location.updated_at).toLocaleTimeString()}`
                                    : ""}
                                </span>
                              </Popup>
                            </Marker>
                          )}
                          
                          {/* Driver Path with Direction Arrows */}
                          {trackingData.location_path && trackingData.location_path.length > 1 && (
                            <>
                              {/* Main path line */}
                              <Polyline
                                positions={trackingData.location_path.map(p => [p.lat, p.lng])}
                                color="#6366f1"
                                weight={4}
                                opacity={0.8}
                              />
                              {/* Arrow markers every few points to show direction */}
                              {trackingData.location_path.map((point, idx) => {
                                if (idx === 0 || idx % 3 !== 0 || idx >= trackingData.location_path.length - 1) return null;
                                const prev = trackingData.location_path[idx - 1];
                                const angle = Math.atan2(point.lng - prev.lng, point.lat - prev.lat) * 180 / Math.PI;
                                return (
                                  <Marker
                                    key={`arrow-${idx}`}
                                    position={[point.lat, point.lng]}
                                    icon={L.divIcon({
                                      className: 'arrow-icon',
                                      html: `<div style="transform: rotate(${angle + 90}deg); color: #6366f1; font-size: 16px; font-weight: bold;">➤</div>`,
                                      iconSize: [20, 20],
                                      iconAnchor: [10, 10]
                                    })}
                                  />
                                );
                              })}
                              {/* Start marker */}
                              {trackingData.location_path.length > 0 && (
                                <Marker
                                  position={[trackingData.location_path[0].lat, trackingData.location_path[0].lng]}
                                  icon={L.divIcon({
                                    className: 'start-icon',
                                    html: `<div style="background: #22c55e; color: white; border-radius: 50%; width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: bold; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);">S</div>`,
                                    iconSize: [24, 24],
                                    iconAnchor: [12, 12]
                                  })}
                                >
                                  <Popup>Driver Start Position</Popup>
                                </Marker>
                              )}
                              {/* End marker */}
                              {trackingData.location_path.length > 1 && (
                                <Marker
                                  position={[trackingData.location_path[trackingData.location_path.length - 1].lat, trackingData.location_path[trackingData.location_path.length - 1].lng]}
                                  icon={L.divIcon({
                                    className: 'end-icon',
                                    html: `<div style="background: #ef4444; color: white; border-radius: 50%; width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: bold; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);">E</div>`,
                                    iconSize: [24, 24],
                                    iconAnchor: [12, 12]
                                  })}
                                >
                                  <Popup>Driver End Position</Popup>
                                </Marker>
                              )}
                            </>
                          )}
                          
                          {/* Auto-fit bounds */}
                          <FitBounds 
                            bounds={[
                              ...(trackingData.pickup?.lat ? [[trackingData.pickup.lat, trackingData.pickup.lng]] : []),
                              ...(trackingData.dropoff?.lat ? [[trackingData.dropoff.lat, trackingData.dropoff.lng]] : []),
                              ...(trackingData.location_path || []).map(p => [p.lat, p.lng])
                            ]}
                          />
                        </MapContainer>
                      </div>
                      
                      {/* Legend */}
                      <div className="flex flex-wrap items-center justify-center gap-3 text-xs">
                        <span className="flex items-center gap-1">
                          <span className="w-3 h-3 rounded-full bg-green-500"></span> Pickup
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="w-3 h-3 rounded-full bg-red-500"></span> Drop
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="w-3 h-3 rounded-full bg-blue-500"></span> Driver Now
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="w-5 h-5 rounded-full bg-green-500 text-white text-[10px] flex items-center justify-center">S</span> Path Start
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="w-5 h-5 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">E</span> Path End
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="text-indigo-600">➤</span> Direction
                        </span>
                      </div>
                      
                      {/* No tracking data message */}
                      {(!trackingData.location_path || trackingData.location_path.length === 0) && (
                        <div className="text-center py-4 bg-yellow-50 rounded-lg">
                          <p className="text-sm text-yellow-700">
                            No location tracking data available for this ride.
                          </p>
                          <p className="text-xs text-yellow-600 mt-1">
                            Driver may not have shared location or tracking was not enabled.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Nearest Drivers for Pending Rides */}
              {selectedRide.status === "pending" && selectedRide.ride_type !== "food_delivery" && (
                <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-blue-700 dark:text-blue-300 flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Nearest {selectedRide.vehicle_type} Riders
                    </h4>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => fetchNearestDrivers(selectedRide)}
                      disabled={loadingNearestDrivers}
                    >
                      <RefreshCw className={`w-4 h-4 ${loadingNearestDrivers ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                  
                  {loadingNearestDrivers ? (
                    <div className="flex items-center justify-center py-4">
                      <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : nearestDrivers.length > 0 ? (
                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                      {nearestDrivers.map((driver, idx) => (
                        <div 
                          key={driver.id} 
                          className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded-lg border"
                        >
                          <div className="flex items-center gap-3">
                            <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center">
                              {idx + 1}
                            </span>
                            <div>
                              <p className="font-medium text-sm">{driver.name}</p>
                              <p className="text-xs text-muted-foreground">{driver.vehicle_number}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-blue-600">{driver.distance_km} km</span>
                            <a 
                              href={`tel:${driver.phone}`}
                              className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium hover:bg-green-200"
                            >
                              <Phone className="w-3 h-3" />
                              {driver.phone}
                            </a>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              onClick={() => {
                                setSelectedDriver(driver.id);
                                setShowAssignDialog(true);
                              }}
                            >
                              Assign
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No online {selectedRide.vehicle_type} riders found nearby
                    </p>
                  )}
                </div>
              )}

              {/* Nearby Food Delivery Riders - For unassigned food delivery orders */}
              {selectedRide.ride_type === "food_delivery" && !selectedRide.driver_id && (
                <div className="p-4 bg-orange-50 dark:bg-orange-950/30 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-orange-700 dark:text-orange-300 flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      Nearby Food Delivery Riders
                    </h4>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => fetchFoodDeliveryNearbyRiders(selectedRide)}
                      disabled={loadingFoodDeliveryRiders}
                    >
                      <RefreshCw className={`w-4 h-4 ${loadingFoodDeliveryRiders ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                  <p className="text-xs text-orange-600 mb-3">Only showing bike riders with Food Delivery enabled</p>
                  
                  {loadingFoodDeliveryRiders ? (
                    <div className="flex items-center justify-center py-4">
                      <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : foodDeliveryNearbyRiders.length > 0 ? (
                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                      {foodDeliveryNearbyRiders.map((rider, idx) => (
                        <div 
                          key={rider.id} 
                          className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded-lg border"
                        >
                          <div className="flex items-center gap-3">
                            <span className="w-6 h-6 rounded-full bg-orange-100 text-orange-700 text-xs font-bold flex items-center justify-center">
                              {idx + 1}
                            </span>
                            <div>
                              <p className="font-medium text-sm">{rider.name}</p>
                              <p className="text-xs text-muted-foreground">{rider.vehicle_number}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-orange-600">{rider.distance_km} km</span>
                            <a 
                              href={`tel:${rider.phone}`}
                              className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium hover:bg-green-200"
                            >
                              <Phone className="w-3 h-3" />
                              {rider.phone}
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No online food delivery riders found nearby
                    </p>
                  )}
                </div>
              )}

              {/* Admin Actions */}
              {!["completed", "cancelled"].includes(selectedRide.status) && (
                <div className="pt-4 border-t space-y-3">
                  <p className="text-sm font-medium text-muted-foreground">Admin Actions</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedRide.status === "pending" && (
                      <Button onClick={() => setShowAssignDialog(true)}>
                        <UserPlus className="w-4 h-4 mr-2" /> Assign Driver
                      </Button>
                    )}
                    {selectedRide.status === "accepted" && (
                      <Button onClick={() => handleUpdateStatus("arrived")} disabled={actionLoading}>
                        <MapPin className="w-4 h-4 mr-2" /> Mark Arrived
                      </Button>
                    )}
                    {selectedRide.status === "arrived" && (
                      <Button onClick={() => handleUpdateStatus("in_progress")} disabled={actionLoading}>
                        <Clock className="w-4 h-4 mr-2" /> Start Ride
                      </Button>
                    )}
                    {["accepted", "arrived", "in_progress"].includes(selectedRide.status) && (
                      <Button variant="default" className="bg-green-600 hover:bg-green-700" onClick={() => setShowCompleteDialog(true)}>
                        <CheckCircle className="w-4 h-4 mr-2" /> Complete Ride
                      </Button>
                    )}
                    <Button variant="destructive" onClick={() => setShowCancelDialog(true)}>
                      <XCircle className="w-4 h-4 mr-2" /> Cancel Ride
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign Driver Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Driver</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>Select Driver</Label>
            <Select value={selectedDriver} onValueChange={setSelectedDriver}>
              <SelectTrigger className="mt-2">
                <SelectValue placeholder="Choose a driver" />
              </SelectTrigger>
              <SelectContent>
                {availableDrivers.map((driver) => (
                  <SelectItem key={driver.id} value={driver.id}>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${driver.is_online ? "bg-green-500" : "bg-gray-300"}`} />
                      {driver.name} - {driver.vehicle_number} ({driver.rating}★)
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {availableDrivers.length === 0 && (
              <p className="text-sm text-muted-foreground mt-2">No available drivers for this vehicle type</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>Cancel</Button>
            <Button onClick={handleAssignDriver} disabled={actionLoading || !selectedDriver}>
              {actionLoading ? "Assigning..." : "Assign Driver"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete Ride Dialog */}
      <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Complete Ride</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>Final Fare (optional)</Label>
              <Input
                type="number"
                value={finalFare}
                onChange={(e) => setFinalFare(e.target.value)}
                placeholder={`Current: ₹${selectedRide?.fare || 0}`}
                className="mt-2"
              />
              <p className="text-xs text-muted-foreground mt-1">Leave empty to use original fare</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompleteDialog(false)}>Cancel</Button>
            <Button onClick={handleCompleteRide} disabled={actionLoading} className="bg-green-600 hover:bg-green-700">
              {actionLoading ? "Completing..." : "Complete Ride"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Ride Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Ride</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>Cancellation Reason *</Label>
              <Input
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="e.g., Driver unavailable, Customer request..."
                className="mt-2"
              />
              <p className="text-xs text-muted-foreground mt-1">This message will be shown to the customer</p>
            </div>
            <div>
              <Label>Refund Amount (optional)</Label>
              <Input
                type="number"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                placeholder="₹0"
                className="mt-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelDialog(false)}>Back</Button>
            <Button variant="destructive" onClick={handleCancelRide} disabled={actionLoading || !cancelReason.trim()}>
              {actionLoading ? "Cancelling..." : "Cancel Ride"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RidesManagement;
